'use strict'

const flow_ = require('lodash/flow')
const isPlainObject_ = require('lodash/isPlainObject')
const includes_ = require('lodash/fp/includes')
const curry_ = require('lodash/curry')
const maybe = require('folktale/maybe')

const queryResults = require('../helpers/queryResults')

const { assign, bind } = require('./utils')

const {
  cleanseRecord,
  createDraft,
  getRecordId,
  markRecordDirty
} = require('./records')

// freshCollection :: () -> Collection
const freshCollection = () => ({
  records: {},
  drafts: {},
  scopes: {}
})

// insertToObjectMap :: ((Object -> Object), Id) -> Object -> Object
const insertToObjectMap = (accessor, idFn, items) => object =>
  Object.assign(
    {},
    accessor(object),
    ...items.map(item => ({ [idFn(item)]: item }))
  )

// updateInObjectMap :: ((Object -> Object), Id, Record -> Record) -> Object -> Object
const updateInObjectMap = (accessor, id, updateFn) => object =>
  Object.assign({}, accessor(object), { [id]: updateFn(accessor(object)[id]) })

// removeFromObjectMap :: ((Object -> Object), Id) -> Object -> Object
const removeFromObjectMap = (accessor, id) => object =>
  Object.assign(
    {},
    ...Object.keys(accessor(object))
      .filter(mapId => mapId !== id)
      .map(mapId => ({ [mapId]: accessor(object)[mapId] }))
  )

// setCollectionRecords :: RecordsObject -> Collection -> Collection
const setCollectionRecords = records => assign({ records })

// setDrafts :: DraftsObject -> Collection -> Collection
const setDrafts = drafts => assign({ drafts })

// setScope :: (ScopeKey, Scope) -> Collection -> Collection
const setScope = curry_((key, scope, collection) =>
  assign({
    scopes: Object.assign({}, collection.scopes, { [key]: scope })
  })(collection)
)

// getScope :: ScopeKey -> Collection -> Scope
const getScope = curry_((scopeKey, collection) => collection.scopes[scopeKey])

// iterateScopes :: ((Scope, ScopeKey) -> *, (Scope, ScopeKey) -> Bool) -> Collection -> [*]
const iterateScopes = curry_((iterateFn, predicate, collection) =>
  Object.keys(collection.scopes)
    .filter(key => predicate(collection.scopes[key], key))
    .map(key => iterateFn(collection.scopes[key], key))
)

// insertRecords :: [Record] -> Collection -> Collection
const insertRecords = records =>
  bind(
    insertToObjectMap(({ records }) => records, getRecordId, records),
    setCollectionRecords
  )

// insertRecord :: Record -> Collection -> Collection
const insertRecord = record => insertRecords([record])

// insertDrafts :: [Draft] -> Collection -> Collection
const insertDrafts = drafts =>
  bind(
    insertToObjectMap(({ drafts }) => drafts, getRecordId, drafts),
    setDrafts
  )

// insertDraft :: Draft -> Collection -> Collection
const insertDraft = draft => insertDrafts([draft])

// removeRecord :: RecordId -> Collection -> Collection
const removeRecord = recordId =>
  bind(
    removeFromObjectMap(({ records }) => records, recordId),
    setCollectionRecords
  )

// removeDraft :: Draft -> Collection -> Collection
const removeDraft = draft =>
  bind(
    removeFromObjectMap(({ drafts }) => drafts, getRecordId(draft)),
    setDrafts
  )

// resetDraft :: (Draft, DefaultDraft) -> Collection -> Collection
const resetDraft = (draft, defaultDraft) =>
  bind(
    updateInObjectMap(({ drafts }) => drafts, getRecordId(draft), d =>
      createDraft(d, defaultDraft)
    ),
    setDrafts
  )

// updateScope :: ScopeKey -> (Scope -> Scope) -> Collection -> Collection
const updateScope = curry_((scopeKey, updateFn) =>
  bind(flow_(getScope(scopeKey), updateFn), setScope(scopeKey))
)

// storeQueryResults :: QueryResults -> Collection -> Collection
const storeQueryResults = queryResult =>
  queryResult.matchWith({
    Empty: () => _ => _,
    Results: ({ items, totalCount, offset }) =>
      items.length > 0 ? insertRecords(items) : _ => _
  })

// updateRecordFields :: (RecordId, FieldsValues) -> Collection -> Collection
const updateRecordFields = (recordId, fieldValues) =>
  bind(
    updateInObjectMap(({ drafts }) => drafts, recordId, draft =>
      Object.assign({}, markRecordDirty(draft), fieldValues)
    ),
    setDrafts
  )

// doesRecordExist :: (Id, Collection) -> Bool
const doesRecordExist = (id, collection) => !!collection.records[id]

// getDraftOrRecord :: (RecordOrDraftId, Collection) -> Maybe MergedRecord
const getDraftOrRecord = (id, collection) => {
  if (isPlainObject_(collection.records[id])) {
    const mergedRecord = Object.assign(
      {},
      collection.records[id],
      collection.drafts[id]
    )

    return maybe.Just(mergedRecord)
  } else {
    return isPlainObject_(collection.drafts[id])
      ? maybe.Just(Object.assign({}, collection.drafts[id]))
      : maybe.Nothing()
  }
}

// readFromCollection :: ScopeKey -> Integer -> Integer -> Collection -> Boolean -> QueryResults
const readFromCollection = curry_(
  (scopeKey, fromIndex, toIndex, collection, allowMissing = false) => {
    const scope = getScope(scopeKey, collection)
    const numRequestedRecords = toIndex - fromIndex
    const matchingRecords = scope.records.slice(fromIndex, toIndex)

    const hasRequestedMatchingItems = ({ items }) =>
      allowMissing ||
      (numRequestedRecords > 0 && items.length >= numRequestedRecords)
    const isNotSparse = ({ items }) =>
      !(includes_(null, items) || includes_(undefined, items))

    return queryResults
      .of({
        items: matchingRecords,
        totalCount: scope.numMatchingRecords,
        offset: fromIndex
      })
      .filter(hasRequestedMatchingItems)
      .map(({ items, totalCount, offset }) => ({
        items: items.map(id =>
          cleanseRecord(getDraftOrRecord(id, collection).getOrElse({}))
        ),
        totalCount,
        offset
      }))
      .filter(isNotSparse)
  }
)

module.exports.doesRecordExist = doesRecordExist
module.exports.freshCollection = freshCollection
module.exports.getDraftOrRecord = getDraftOrRecord
module.exports.getScope = getScope
module.exports.insertDraft = insertDraft
module.exports.insertRecord = insertRecord
module.exports.iterateScopes = iterateScopes
module.exports.readFromCollection = readFromCollection
module.exports.removeDraft = removeDraft
module.exports.removeRecord = removeRecord
module.exports.resetDraft = resetDraft
module.exports.setScope = setScope
module.exports.storeQueryResults = storeQueryResults
module.exports.updateRecordFields = updateRecordFields
module.exports.updateScope = updateScope



// WEBPACK FOOTER //
// ./recordStore/collections.js